import { useEffect, useRef, useState } from 'react'

import { FRESH_HIGHLIGHT_MS } from 'features/TeamChat/teamChat.constants'

type WithId = { id: number }

// Ids that arrived after the first load, highlighted briefly so a live change is noticed.
export const useFreshIds = (items: WithId[], loaded: boolean) => {
  const known = useRef<Set<number> | null>(null)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const [fresh, setFresh] = useState<ReadonlySet<number>>(new Set())

  useEffect(() => {
    if (!loaded) return
    const ids = items.map((i) => i.id)
    if (!known.current) {
      known.current = new Set(ids)
      return
    }
    const seen = known.current
    const added = ids.filter((id) => !seen.has(id))
    if (added.length === 0) return
    known.current = new Set([...seen, ...added])
    setFresh((prev) => new Set([...prev, ...added]))
    const timer = setTimeout(() => {
      timers.current = timers.current.filter((t) => t !== timer)
      setFresh((prev) => new Set([...prev].filter((id) => !added.includes(id))))
    }, FRESH_HIGHLIGHT_MS)
    timers.current = [...timers.current, timer]
  }, [items, loaded])

  useEffect(() => () => timers.current.forEach(clearTimeout), [])

  return (id: number) => fresh.has(id)
}
